import React, { useRef, useState } from "react";

import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";

import FarmDialog from "@components/Layout/Farms/FarmDialog";
import LeftBarSettingsMenuItem from "@components/Layout/LeftBar/LeftBarSettingsMenuItem";
import Text from "@components/Text";
import useOnClickOutside from "@src/hooks/useOnClickOutside";
import { Divider } from "@components/Layout/LeftBar/LeftBarSettings";
import { ReactComponent as DirectionIcon } from "@assets/icons/direction.svg";
import { ReactComponent as PlusIcon } from "@assets/icons/PlusIcon.svg";
import { ReactComponent as EditIcon } from "@assets/icons/edit.svg";
import { Row } from "@components/Flex";
import { useLang } from "@src/hooks/useLang";
import { useFarmsVM } from "@stores/FarmStore";
import { useStores } from "@stores";
import { Farm } from "@services/farmsService";

interface IProps {
  expanded: boolean;
  menuWidth: number;
}

const Root = styled(Row)`
  display: flex;
  align-items: center;
  height: 38px;
  cursor: pointer;
  margin-top: 6px;
  overflow: hidden;
`;

const FarmLabel = styled(Text)`
  font-size: 9px;
  line-height: 0;
  padding-bottom: 2px;
`;

const Tooltip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: absolute;
  transition: 0.4s;
  overflow: hidden;
  top: 0;
  background: #ffffff;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.2);
  border-radius: 6px 6px 6px 0;
`;

const FarmsImpl: React.FC<IProps> = observer(({ expanded, menuWidth }) => {
  const lang = useLang();
  const vm = useFarmsVM();
  const { accountStore } = useStores();
  // Farms are the scoping boundary — only the account owner can create/rename/
  // delete them. Members can still switch between the farms they can access.
  const canManageFarms = accountStore.isViewingOwnAccount;
  // Free accounts get exactly one farm; creating more is a paid feature
  // (backend enforces the same rule in farmService.createFarm).
  const canCreateFarm = canManageFarms && accountStore.subscriptionPaid;

  const [tooltipOpened, setTooltipOpened] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "rename" | null>(null);
  const [renameTarget, setRenameTarget] = useState<Farm | null>(null);

  const offset = expanded ? menuWidth : 52;
  const ref = useRef(null);
  useOnClickOutside(ref, () => setTooltipOpened(false));

  if (vm.loading || !expanded) {
    return null;
  }

  const current = vm.currentFarm;

  return (
    <div style={{ position: "relative" }}>
      <Root justifyContent="space-between">
        <div style={{ width: "100%" }} ref={ref}>
          <Row
            alignItems="center"
            justifyContent="space-between"
            onClick={() => setTooltipOpened(!tooltipOpened)}
          >
            <div style={{ width: "100%" }}>
              <FarmLabel type="heading" color="#899093">
                {lang.menu.farmTitle}
              </FarmLabel>
              <Text type="heading" color="#494C5B" size={12}>
                {current ? current.name : lang.farms.defaultName}
              </Text>
            </div>
            <DirectionIcon style={{ minWidth: 24 }} />
          </Row>
          <Tooltip style={{ left: offset - 12, width: tooltipOpened ? 208 : 0 }}>
            {vm.farms.map((f) => {
              const active = f.id === vm.currentFarmId;
              return (
                <LeftBarSettingsMenuItem
                  key={f.id}
                  style={{ cursor: active ? "default" : "pointer" }}
                  className={active ? "active" : ""}
                  title={f.name}
                  onClick={() => {
                    if (!active) vm.setCurrentFarmId(f.id);
                    setTooltipOpened(false);
                  }}
                  icon={
                    canManageFarms ? (
                      <EditIcon
                        style={{ flexShrink: 0, cursor: "pointer", marginRight: 16 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenameTarget(f);
                          setDialogMode("rename");
                          setTooltipOpened(false);
                        }}
                      />
                    ) : undefined
                  }
                />
              );
            })}
            {canCreateFarm && (
              <>
                <Divider />
                <LeftBarSettingsMenuItem
                  onClick={() => {
                    setDialogMode("create");
                    setTooltipOpened(false);
                  }}
                  title={lang.menu.newFarmButton}
                  icon={<PlusIcon style={{ marginRight: 16 }} />}
                />
              </>
            )}
          </Tooltip>
        </div>
      </Root>

      <FarmDialog
        visible={dialogMode !== null}
        mode={dialogMode ?? "create"}
        initialName={dialogMode === "rename" ? renameTarget?.name : ""}
        onClose={() => {
          setDialogMode(null);
          setRenameTarget(null);
        }}
        onDelete={
          dialogMode === "rename" && renameTarget && !renameTarget.isDefault
            ? () => {
                if (!window.confirm(lang.farms.deleteConfirm)) return;
                vm.deleteFarm(renameTarget.id)
                  .then(() => {
                    setDialogMode(null);
                    setRenameTarget(null);
                  })
                  .catch((e) => {
                    toast.error(e?.message ?? e.toString());
                  });
              }
            : undefined
        }
        onSubmit={(name) => {
          const action =
            dialogMode === "rename" && renameTarget
              ? vm.renameFarm(renameTarget.id, name)
              : vm.createFarm(name);
          action
            .then(() => {
              setDialogMode(null);
              setRenameTarget(null);
            })
            .catch((e) => {
              console.error("Farm action failed", e);
              toast.error(e?.message ?? e.toString());
            });
        }}
      />
    </div>
  );
});

const Farms: React.FC<IProps> = (props) => <FarmsImpl {...props} />;

export default observer(Farms);
